/* ==========================================================================================
   Commercial Equipment Online 2026 · ce-cart-agree.js · the cart's ONE required tick box (snippets/ce-cart-agree).
   Product builder, 09-10-2026. Deferred, no dependencies. Works on the cart drawer and the cart page, and survives
   Dawn re-drawing the drawer after every change (one set of listeners on the document, nothing bound to a node).

   1. Ticking it SAVES it on the order at once: cart attribute "Terms and Fit Check" = "Yes, ticked <date, time,
      Brisbane time>: <the sentence>" (POST /cart/update.js). The checkbox carries the same value, so the checkout
      form sends it again. Unticking clears it.
   2. CHECKOUT STAYS BLOCKED until it is ticked: every Check out button (drawer, cart page, the phone checkout bar)
      and every checkout form submit is stopped in the capture phase, the message shows under the box and the box is
      brought into view. Nothing else in the cart is touched.
   3. The T&C app (Effective Apps, a ScriptTag) still draws its own box on the cart page until it is switched off at
      launch. Two boxes must never show: its box is hidden on this theme and ticked IN STEP with ours (its own gate then
      passes, and it keeps writing its "Agreed to the Terms and Conditions on" attribute as on the live site).
   4. BUSINESS OR HOME (23-10-2026, hub A943): the question above the box (snippets/ce-cart-agree). "A business" needs
      the business name, "A home" does not; checkout stays blocked until it is answered, through the same gate. Saved
      as cart attributes "Delivering to" + "Business name" ("" for a home), on every change and again before a link
      checkout leaves with an unsaved answer. The hub copies the name into the order's delivery address.
   ========================================================================================== */
(() => {
  'use strict';
  if (window.CECartAgree) return;
  const W = window, D = document;
  const KEY = 'Terms and Fit Check';
  const boxes = () => Array.prototype.slice.call(D.querySelectorAll('[data-cca-box]'));
  const anyTicked = () => boxes().some((b) => b.checked);
  const stamp = () => {
    let when = '';
    try {
      when = new Intl.DateTimeFormat('en-AU', { timeZone: 'Australia/Brisbane', day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date()) + ' (Brisbane time)';
    } catch (e) { when = new Date().toISOString(); }
    return when;
  };
  const textOf = (box) => {
    const wrap = box.closest('[data-cca]');
    return (wrap && wrap.getAttribute('data-cca-text')) || '';
  };
  // ONE cart write at a time, ours included (two sent together can undo each other - see 1. below)
  let queue = Promise.resolve();
  const post = (attributes) => {
    queue = queue.then(() => fetch(((W.routes && W.routes.cart_update_url) || '/cart/update') + '.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, credentials: 'same-origin',
      body: JSON.stringify({ attributes })
    })).catch(() => { /* the checkout form sends it again */ });
    return queue;
  };
  const save = (on, value) => {
    const attributes = {};
    attributes[KEY] = on ? value : '';
    return post(attributes);
  };

  // 4. business or home: one answer for every copy of the question (drawer + page), kept here so a drawer re-draw
  // never loses a name that is typed but not saved yet
  const SITE_KEY = 'Delivering to', NAME_KEY = 'Business name', BUSINESS = 'A business';
  const site = { value: '', name: '', saved: null };
  const siteWraps = () => Array.prototype.slice.call(D.querySelectorAll('[data-ccs]'));
  const readSite = () => {
    const first = siteWraps()[0];
    if (!first) return;
    const picked = D.querySelector('[data-ccs-site]:checked');
    site.value = picked ? picked.value : '';
    const named = Array.prototype.slice.call(D.querySelectorAll('[data-ccs-name]')).find((i) => i.value.trim());
    site.name = named ? named.value.trim() : '';
    // what the cart held when the page was drawn; after that, what we last wrote (saveSite)
    site.saved = { value: first.getAttribute('data-ccs-saved-site') || '', name: first.getAttribute('data-ccs-saved-name') || '' };
  };
  const wanted = () => ({ value: site.value, name: site.value === BUSINESS ? site.name : '' });
  const dirty = () => !!site.saved && (site.saved.value !== wanted().value || site.saved.name !== wanted().name);
  const saveSite = () => {
    const want = wanted();
    const attributes = {};
    attributes[SITE_KEY] = want.value;
    attributes[NAME_KEY] = want.name;
    return post(attributes).then(() => { site.saved = want; });
  };
  // write the answer into every copy on the page (and any copy a re-draw just put back)
  const paintSite = () => {
    siteWraps().forEach((wrap) => {
      wrap.querySelectorAll('[data-ccs-site]').forEach((r) => { if (r.checked !== (r.value === site.value)) r.checked = r.value === site.value; });
      const business = site.value === BUSINESS;
      const biz = wrap.querySelector('[data-ccs-biz]');
      if (biz && biz.hidden === business) biz.hidden = !business;
      const input = wrap.querySelector('[data-ccs-name]');
      if (input) {
        if (input.disabled === business) input.disabled = !business;   // a disabled box is not sent with the form
        if (D.activeElement !== input && input.value.trim() !== site.name) input.value = site.name;
      }
    });
  };
  const siteProblem = () => {
    if (!siteWraps().length) return '';
    if (!site.value) return 'site';
    if (site.value === BUSINESS && site.name.length < 2) return 'name';
    return '';
  };
  const SITE_WORDS = {
    site: 'Choose business or home before you check out.',
    name: 'Add your shop or trading name before you check out. The courier needs it to find you.'
  };
  const showSiteError = (problem, near) => {
    siteWraps().forEach((wrap) => {
      const err = wrap.querySelector('[data-ccs-error]');
      if (err) { err.textContent = problem ? SITE_WORDS[problem] : ''; err.hidden = !problem; }
      wrap.classList.toggle('is-error', problem === 'site');
      wrap.classList.toggle('is-error-name', problem === 'name');
    });
    if (problem && near) {
      try { near.scrollIntoView({ block: 'center', behavior: W.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }); } catch (e) { near.scrollIntoView(); }
      const target = problem === 'name' ? near.querySelector('[data-ccs-name]') : near.querySelector('[data-ccs-site]');
      if (target) setTimeout(() => target.focus({ preventScroll: true }), 250);
    }
  };
  let nameTimer = null;
  D.addEventListener('change', (e) => {
    const radio = e.target.closest && e.target.closest('[data-ccs-site]');
    if (!radio) return;
    if (!site.saved) readSite();
    site.value = radio.value;
    paintSite();
    showSiteError('');
    saveSite();
  });
  D.addEventListener('input', (e) => {
    const input = e.target.closest && e.target.closest('[data-ccs-name]');
    if (!input) return;
    if (!site.saved) readSite();
    site.name = input.value.trim();
    if (site.name.length >= 2) showSiteError('');
    clearTimeout(nameTimer);
    nameTimer = setTimeout(saveSite, 600);
  });
  // the T&C app's box(es): hidden and kept in step
  const tncBoxes = () => Array.prototype.slice.call(D.querySelectorAll('[id="effectiveAppsAgreeCB"]'));
  const mirrorTnc = (on) => {
    tncBoxes().forEach((cb) => {
      const holder = cb.closest('p') || cb.parentElement;
      if (holder && !holder.classList.contains('cca-tnc-hidden')) holder.classList.add('cca-tnc-hidden');
      if (cb.checked !== on) {
        cb.checked = on;
        try { cb.dispatchEvent(new Event('change', { bubbles: true })); } catch (e) { /* fine */ }
      }
    });
  };
  const showError = (show, near) => {
    D.querySelectorAll('[data-cca]').forEach((wrap) => {
      const err = wrap.querySelector('[data-cca-error]');
      if (err) err.hidden = !show;
      wrap.classList.toggle('is-error', show);
    });
    if (show && near) {
      try { near.scrollIntoView({ block: 'center', behavior: W.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }); } catch (e) { near.scrollIntoView(); }
      const box = near.querySelector('[data-cca-box]');
      if (box) setTimeout(() => box.focus({ preventScroll: true }), 250);
    }
  };

  // 1. ticking it
  D.addEventListener('change', (e) => {
    const box = e.target.closest && e.target.closest('[data-cca-box]');
    if (!box) return;
    const on = box.checked;
    const value = on ? 'Yes, ticked ' + stamp() + ': ' + textOf(box) : '';
    boxes().forEach((b) => { b.checked = on; if (on) b.value = value; });
    if (on) showError(false);
    // ONE write at a time: the T&C app writes its own attribute when its box changes, and two cart updates sent
    // together can undo each other (measured: ours was lost once). Ours first, then the app's box, then a check.
    save(on, value).then(() => {
      mirrorTnc(on);
      setTimeout(() => verify(on, value), 1600);
    });
  });
  // the attribute really is on the cart (or really is gone): write it again if another write undid it
  const verify = (on, value) => {
    if (anyTicked() !== on) return;                     // the shopper has changed it since
    fetch(((W.routes && W.routes.cart_url) || '/cart') + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((c) => {
        const has = !!(c && c.attributes && c.attributes[KEY]);
        if (on !== has) save(on, value);
      })
      .catch(() => { /* the checkout form sends it anyway */ });
  };

  // 2. checkout stays blocked until it is ticked
  // every way to checkout on this theme, including the T&C app's CLONE of our button (it hides ours in a span and
  // puts a type="button" copy with an inline onclick in its place, [tac-co-cloned]); the capture phase runs first
  const TRIGGERS = '[name="checkout"], #CartDrawer-Checkout, #checkout, .cart__checkout-button, [tac-co-cloned], .ce-cs-bar__btn, a[href$="/checkout"], a[href*="/checkout?"]';
  const nearestWrap = (el, sel) => {
    const drawer = el.closest('cart-drawer, .drawer');
    const scope = drawer || D;
    return scope.querySelector(sel || '[data-cca]');
  };
  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
  };
  const block = (e, el) => {
    if (!D.querySelector('[data-cca]')) return false;      // no box on this page: nothing to enforce here
    // business or home first: it sits above the box, so the shopper is taken to the first thing missing
    if (siteWraps().length) {
      if (!site.saved) readSite();        // after the first read, the answer kept here is the truth, not a re-drawn copy
      const problem = siteProblem();
      if (problem) {
        stop(e);
        showSiteError(problem, nearestWrap(el, '[data-ccs]'));
        if (!anyTicked()) showError(true, null);
        return true;
      }
    }
    if (!anyTicked()) {
      stop(e);
      showError(true, nearestWrap(el));
      return true;
    }
    mirrorTnc(true);
    // a LINK to checkout sends no form, so an answer typed in the last moment is saved first
    if (dirty() && !(el.matches && el.matches('[name="checkout"], form'))) {
      stop(e);
      clearTimeout(nameTimer);
      saveSite().then(() => { W.location.href = (W.routes && W.routes.root || '/').replace(/\/?$/, '/') + 'checkout'; });
      return true;
    }
    return false;
  };
  D.addEventListener('click', (e) => {
    const t = e.target.closest && e.target.closest(TRIGGERS);
    if (!t) return;
    block(e, t);
  }, true);
  D.addEventListener('submit', (e) => {
    const f = e.target;
    if (!f || !f.matches || !f.matches('#cart, #CartDrawer-Form, form[action*="/cart"]')) return;
    const sub = e.submitter;
    const toCheckout = (sub && sub.getAttribute('name') === 'checkout') || f.querySelector('input[name="checkout"]');
    if (!toCheckout) return;
    block(e, sub || f);
  }, true);

  // 3. the T&C app draws late: keep its box hidden and in step as it appears
  const sync = () => {
    const on = anyTicked();
    if (tncBoxes().length) mirrorTnc(on);
    // a re-drawn drawer comes back from the server's copy of the cart: put the shopper's answer back on it
    if (siteWraps().length) {
      if (!site.saved) readSite();
      else paintSite();
    }
  };
  // (mobile audit 10-10: a resetting debounce here was starved by the drawer's constant DOM changes, so the app's box
  // stayed unsynced for seconds; now it runs at most every 80 ms and is never pushed back)
  if (W.MutationObserver) {
    let pending = false;
    new MutationObserver(() => {
      if (pending) return;
      pending = true;
      setTimeout(() => { pending = false; sync(); }, 80);
    }).observe(D.documentElement, { childList: true, subtree: true });
  }
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', sync); else sync();
  W.CECartAgree = { anyTicked, sync, site };
})();
